/**
 * Lista de conversas com filtros por status, departamento e inbox
 * 
 * Exibe conversas com avatar, preview, badges de inbox e departamento
 * Inclui seletor de inbox e pesquisa colapsável
 * 
 * @version 2025.01.07 - Suporte a multi-seleção de status com checkboxes
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Search, Filter, MessageSquare, Building, ChevronDown, Users, User, Tag, Megaphone, Kanban, Inbox, X, Instagram, Send, Globe, MessagesSquare, Plus, SlidersHorizontal, UserCircle, LayoutGrid, Check, Mail, MailOpen, Eye, Zap, Loader2, UserX, Bot, Pin } from 'lucide-react';
import { WhatsAppStatusIcon } from '@/components/icons/WhatsAppStatusIcon';
import { WhatsAppStatusSheet } from './WhatsAppStatusSheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Conversation } from '@/pages/tenant/Chat';
import { Database } from '@/integrations/supabase/types';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { getStorageUrl } from '@/lib/storage';
// Remove os UUIDs das @menções (`@[Nome](uuid)` → `@Nome`) no preview
import { stripMentionIds } from '@/hooks/useActivityComments';
import { StartNewConversationDialog } from './StartNewConversationDialog';

type ConversationStatus = Database['public']['Enums']['conversation_status'];
export type ConversationType = 'all' | 'individual' | 'group';

// Interface para departamento (usado no filtro)
export interface Department {
  id: string;
  name: string;
}

// Interface para inbox (usado no seletor)
// Inclui status para indicador visual de conexão
export interface InboxOption {
  id: string;
  name: string;
  channel_type: string;
  identifier: string;
  status?: string;
}

// Interface para agente (filtro avançado)
export interface AgentOption {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
}

// Interface para tag (filtro avançado)
export interface TagOption {
  id: string;
  name: string;
  color: string;
}

// Interface para fase kanban (filtro avançado)
export interface PhaseOption {
  id: string;
  name: string;
  color: string;
  category_id: string | null;
}

// Interface para categoria kanban (filtro avançado)
export interface CategoryOption {
  id: string;
  name: string;
  color: string;
}

interface ConversationListProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  onSelectConversation: (conversation: Conversation) => void;
  /** Total de conversas retornado pela RPC (total_count via window function) */
  totalCount?: number;
  /** Filtros de status selecionados (multi-select) */
  statusFilters: (ConversationStatus | 'all')[];
  /** Callback quando filtros de status mudam */
  onStatusFiltersChange: (statuses: (ConversationStatus | 'all')[]) => void;
  /** v1.1.0 - Escopo rápido: meus/todos/bot */
  scopeFilter?: 'all' | 'mine' | 'bot';
  /** Callback quando o escopo rápido muda */
  onScopeFilterChange?: (scope: 'all' | 'mine' | 'bot') => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /** Lista de departamentos para filtro */
  departments?: Department[];
  /** Departamento selecionado para filtro */
  departmentFilter?: string | 'all';
  /** Callback quando filtro de departamento muda */
  onDepartmentFilterChange?: (departmentId: string | 'all') => void;
  /** Filtro por tipo de conversa (individual/grupo) */
  conversationTypeFilter?: ConversationType;
  /** Callback quando filtro de tipo muda */
  onConversationTypeFilterChange?: (type: ConversationType) => void;
  /** Filtro por status de leitura (todas/lidas/não lidas) */
  readStatusFilter?: 'all' | 'read' | 'unread';
  /** Callback quando filtro de leitura muda */
  onReadStatusFilterChange?: (status: 'all' | 'read' | 'unread') => void;
  /** Lista de inboxes para seletor */
  inboxes?: InboxOption[];
  /** Inbox selecionada para filtro */
  inboxFilter?: string | 'all';
  /** Callback quando filtro de inbox muda */
  onInboxFilterChange?: (inboxId: string | 'all') => void;
  /** ID do tenant para operações de contato */
  tenantId?: string;
  
  // ===== INFINITE SCROLL =====
  /** Callback para carregar mais conversas (próxima página) */
  onLoadMore?: () => void;
  /** Indica se há mais páginas para carregar */
  hasMore?: boolean;
  /** Indica se está carregando a próxima página */
  isFetchingMore?: boolean;
  
  // ===== FILTROS AVANÇADOS =====
  /** Lista de agentes para filtro avançado */
  agents?: AgentOption[];
  /** Agente selecionado para filtro */
  agentFilter?: string | 'all';
  /** Callback quando filtro de agente muda */
  onAgentFilterChange?: (agentId: string | 'all') => void;
  /** Lista de tags para filtro avançado */
  tags?: TagOption[];
  /** Tag selecionada para filtro */
  tagFilter?: string | 'all';
  /** Callback quando filtro de tag muda */
  onTagFilterChange?: (tagId: string | 'all') => void;
  /** Lista de fases kanban para filtro avançado */
  phases?: PhaseOption[];
  /** Fase selecionada para filtro */
  phaseFilter?: string | 'all';
  /** Callback quando filtro de fase muda */
  onPhaseFilterChange?: (phaseId: string | 'all') => void;
  /** Lista de categorias kanban para filtro avançado */
  categories?: CategoryOption[];
  /** Categoria selecionada para filtro */
  categoryFilter?: string | 'all';
  /** Callback quando filtro de categoria muda */
  onCategoryFilterChange?: (categoryId: string | 'all') => void;
}

const statusLabels: Record<ConversationStatus | 'all', string> = {
  all: 'Todas',
  pending: 'Pendentes',
  open: 'Abertas',
  closed: 'Encerradas',
  spam: 'Spam',
};

const statusColors: Record<ConversationStatus, string> = {
  pending: 'bg-amber-500',
  open: 'bg-green-500',
  closed: 'bg-muted',
  spam: 'bg-destructive',
};

const typeLabels: Record<ConversationType, string> = {
  all: 'Todas',
  individual: 'Individuais',
  group: 'Grupos',
};

// ===== COMPONENTES AUXILIARES PARA FILTROS AVANÇADOS =====

/**
 * Filtro de Agente com busca (Popover + Command)
 */
function AgentFilterPopover({
  agents,
  agentFilter,
  onAgentFilterChange,
}: {
  agents: AgentOption[];
  agentFilter: string | 'all';
  onAgentFilterChange: (agentId: string | 'all') => void;
}) {
  const [open, setOpen] = useState(false);
  
  const selectedAgent = agentFilter === 'all' 
    ? null 
    : agentFilter === 'none'
      ? { id: 'none', full_name: 'Sem agente', avatar_url: null }
      : agents.find(a => a.id === agentFilter);
  
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Agente</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between text-xs font-normal"
          >
            {agentFilter === 'none' ? (
              <div className="flex items-center gap-2">
                <UserX className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Sem agente</span>
              </div>
            ) : selectedAgent ? (
              <div className="flex items-center gap-2 truncate">
                <Avatar className="h-4 w-4">
                  <AvatarImage src={selectedAgent.avatar_url || undefined} />
                  <AvatarFallback className="text-[8px]">
                    {selectedAgent.full_name?.charAt(0) || 'A'}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{selectedAgent.full_name || 'Agente'}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Todos os Agentes</span>
              </div>
            )}
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar agente..." className="h-8 text-xs" />
            <CommandList>
              <CommandEmpty>Nenhum agente encontrado</CommandEmpty>
              <CommandGroup>
                {/* Opção: Todos os Agentes */}
                <CommandItem
                  value="all"
                  onSelect={() => {
                    onAgentFilterChange('all');
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <UserCircle className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Todos os Agentes</span>
                  </div>
                  {agentFilter === 'all' && (
                    <Check className="ml-auto h-3.5 w-3.5" />
                  )}
                </CommandItem>
                {/* Opção: Sem agente (assigned_to IS NULL) */}
                <CommandItem
                  value="none"
                  onSelect={() => {
                    onAgentFilterChange('none');
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <UserX className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Sem agente</span>
                  </div>
                  {agentFilter === 'none' && (
                    <Check className="ml-auto h-3.5 w-3.5" />
                  )}
                </CommandItem>
              </CommandGroup>
              {/* Separador entre opções especiais e lista de agentes */}
              <CommandSeparator />
              <CommandGroup>
                {agents.map(agent => (
                  <CommandItem
                    key={agent.id}
                    value={agent.full_name || agent.id}
                    onSelect={() => {
                      onAgentFilterChange(agent.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-4 w-4">
                        <AvatarImage src={agent.avatar_url || undefined} />
                        <AvatarFallback className="text-[8px]">
                          {agent.full_name?.charAt(0) || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{agent.full_name || 'Agente'}</span>
                    </div>
                    {agentFilter === agent.id && (
                      <Check className="ml-auto h-3.5 w-3.5" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Filtro de Tag com busca (Popover + Command)
 */
function TagFilterPopover({
  tags,
  tagFilter,
  onTagFilterChange,
}: {
  tags: TagOption[];
  tagFilter: string | 'all';
  onTagFilterChange: (tagId: string | 'all') => void;
}) {
  const [open, setOpen] = useState(false);
  
  const selectedTag = tagFilter === 'all' 
    ? null 
    : tags.find(t => t.id === tagFilter);
  
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Tag</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between text-xs font-normal"
          >
            {selectedTag ? (
              <div className="flex items-center gap-2 truncate">
                <div 
                  className="h-3 w-3 rounded-full shrink-0" 
                  style={{ backgroundColor: selectedTag.color }}
                />
                <span className="truncate">{selectedTag.name}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Todas as Tags</span>
              </div>
            )}
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar tag..." className="h-8 text-xs" />
            <CommandList>
              <CommandEmpty>Nenhuma tag encontrada</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all"
                  onSelect={() => {
                    onTagFilterChange('all');
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Todas as Tags</span>
                  </div>
                  {tagFilter === 'all' && (
                    <Check className="ml-auto h-3.5 w-3.5" />
                  )}
                </CommandItem>
                {tags.map(tag => (
                  <CommandItem
                    key={tag.id}
                    value={tag.name}
                    onSelect={() => {
                      onTagFilterChange(tag.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full shrink-0" 
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="truncate">{tag.name}</span>
                    </div>
                    {tagFilter === tag.id && (
                      <Check className="ml-auto h-3.5 w-3.5" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Filtro de Fase com filtragem por categoria
 */
function PhaseFilterSelect({
  phases,
  phaseFilter,
  categoryFilter,
  onPhaseFilterChange,
}: {
  phases: PhaseOption[];
  phaseFilter: string | 'all';
  categoryFilter: string | 'all';
  onPhaseFilterChange: (phaseId: string | 'all') => void;
}) {
  // Filtra fases pela categoria selecionada
  const filteredPhases = useMemo(() => {
    if (categoryFilter === 'all') {
      return phases;
    }
    return phases.filter(p => p.category_id === categoryFilter);
  }, [phases, categoryFilter]);
  
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">Fase</label>
      <Select 
        value={phaseFilter} 
        onValueChange={(value) => onPhaseFilterChange(value as string | 'all')}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Todas as Fases" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <Kanban className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Todas as Fases</span>
            </div>
          </SelectItem>
          {filteredPhases.length > 0 && <SelectSeparator />}
          {filteredPhases.map(phase => (
            <SelectItem key={phase.id} value={phase.id}>
              <div className="flex items-center gap-2">
                <div 
                  className="h-3 w-3 rounded-full" 
                  style={{ backgroundColor: phase.color }}
                />
                <span className="truncate">{phase.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ConversationList({
  conversations,
  selectedConversation,
  onSelectConversation,
  totalCount,
  statusFilters,
  onStatusFiltersChange,
  scopeFilter = 'all',
  onScopeFilterChange,
  searchQuery,
  onSearchChange,
  departments = [],
  departmentFilter = 'all',
  onDepartmentFilterChange,
  conversationTypeFilter = 'all',
  onConversationTypeFilterChange,
  readStatusFilter = 'all',
  onReadStatusFilterChange,
  inboxes = [],
  inboxFilter = 'all',
  onInboxFilterChange,
  tenantId,
  // Infinite scroll
  onLoadMore,
  hasMore = false,
  isFetchingMore = false,
  // Filtros avançados
  agents = [],
  agentFilter = 'all',
  onAgentFilterChange,
  tags = [],
  tagFilter = 'all',
  onTagFilterChange,
  phases = [],
  phaseFilter = 'all',
  onPhaseFilterChange,
  categories = [],
  categoryFilter = 'all',
  onCategoryFilterChange,
}: ConversationListProps) {
  const navigate = useNavigate();
  
  // Refs para infinite scroll: sentinel + container de scroll real
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Ref para o viewport real do ScrollArea (elemento com overflow-auto)
  const scrollViewportRef = useRef<HTMLElement | null>(null);
  
  // =========================================================================
  // Captura o viewport real do Radix ScrollArea (filho direto com overflow)
  // O ref do ScrollArea aponta para Root, mas o observer precisa do Viewport
  // =========================================================================
  useEffect(() => {
    if (scrollContainerRef.current) {
      // O Radix ScrollArea cria um Viewport como primeiro filho direto do Root
      // com data-radix-scroll-area-viewport ou classe contendo overflow
      const viewport = scrollContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
      scrollViewportRef.current = viewport as HTMLElement | null;
    }
  }, []);
  
  // =========================================================================
  // IntersectionObserver para infinite scroll
  // Usa viewport real do ScrollArea como root para detectar intersecção correta
  // (root: null usaria viewport do navegador, incorreto dentro de ScrollArea)
  // =========================================================================
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || !onLoadMore) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Quando o sentinel fica visível e não está carregando, busca mais
        if (entry.isIntersecting && !isFetchingMore) {
          onLoadMore();
        }
      },
      { 
        root: scrollViewportRef.current, // Viewport real de scroll (não Root)
        threshold: 0.1, 
        rootMargin: '200px' // Começa a carregar 200px antes de chegar ao fim
      }
    );
    
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, isFetchingMore]);
  
  // Estado para controlar expansão da pesquisa
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Mantém pesquisa expandida enquanto houver texto no campo
  useEffect(() => {
    if (searchQuery) {
      setIsSearchExpanded(true);
    }
  }, [searchQuery]);
  
  // Estado para controlar expansão dos filtros avançados
  const [isAdvancedFiltersExpanded, setIsAdvancedFiltersExpanded] = useState(false);
  
  // Estado para dialog de nova conversa
  const [newConversationDialogOpen, setNewConversationDialogOpen] = useState(false);

  // Quick Create (?new=1): abre o mesmo modal do botão "+" da lista de conversas.
  useAutoOpenFromQuery('1', () => setNewConversationDialogOpen(true));

  
  // Estado para sheet de status do WhatsApp
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);

  // v1.1.0 - Estado do Sheet de filtros avançados (substitui Collapsible inline)
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  // Contagem de filtros avançados ativos
  const activeAdvancedFiltersCount = [
    agentFilter !== 'all',
    tagFilter !== 'all',
    phaseFilter !== 'all',
    categoryFilter !== 'all',
  ].filter(Boolean).length;

  // v1.1.0 - Total de filtros ativos exibidos no badge do botão "Filtros"
  // (não conta o escopo Meus/Todos/Bot pois ele já tem UI dedicada no segmented)
  const totalActiveFiltersCount =
    activeAdvancedFiltersCount +
    (inboxFilter !== 'all' ? 1 : 0) +
    (!statusFilters.includes('all') ? 1 : 0) +
    (conversationTypeFilter !== 'all' ? 1 : 0) +
    (departmentFilter !== 'all' ? 1 : 0) +
    (readStatusFilter !== 'all' ? 1 : 0);

  /**
   * Constrói a lista de chips de filtros ativos para renderização horizontal.
   * Cada chip expõe um onRemove que reseta apenas o seu próprio filtro.
   * Status recebe destaque visual (tone="primary") por ser o mais usado.
   */
  type FilterChip = {
    key: string;
    label: string;
    tone: 'primary' | 'neutral';
    Icon?: typeof Filter;
    onRemove: () => void;
  };
  const activeFilterChips: FilterChip[] = [];

  // Status (multi)
  if (!statusFilters.includes('all')) {
    const statusLabel =
      statusFilters.length === 1
        ? statusLabels[statusFilters[0] as ConversationStatus]
        : `${statusFilters.length} status`;
    activeFilterChips.push({
      key: 'status',
      label: statusLabel,
      tone: 'primary',
      Icon: Filter,
      onRemove: () => onStatusFiltersChange(['all']),
    });
  }
  // Inbox
  if (inboxFilter !== 'all') {
    const inboxName = inboxes.find((i) => i.id === inboxFilter)?.name || 'Caixa';
    activeFilterChips.push({
      key: 'inbox',
      label: inboxName,
      tone: 'neutral',
      Icon: Inbox,
      onRemove: () => onInboxFilterChange?.('all'),
    });
  }
  // Departamento
  if (departmentFilter !== 'all' && onDepartmentFilterChange) {
    const deptName =
      departmentFilter === 'none'
        ? 'Sem departamento'
        : departments.find((d) => d.id === departmentFilter)?.name || 'Departamento';
    activeFilterChips.push({
      key: 'dept',
      label: deptName,
      tone: 'neutral',
      Icon: Building,
      onRemove: () => onDepartmentFilterChange('all'),
    });
  }
  // Tipo
  if (conversationTypeFilter !== 'all' && onConversationTypeFilterChange) {
    activeFilterChips.push({
      key: 'type',
      label: typeLabels[conversationTypeFilter],
      tone: 'neutral',
      Icon: conversationTypeFilter === 'group' ? Users : User,
      onRemove: () => onConversationTypeFilterChange('all'),
    });
  }
  // Leitura
  if (readStatusFilter !== 'all' && onReadStatusFilterChange) {
    activeFilterChips.push({
      key: 'read',
      label: readStatusFilter === 'unread' ? 'Não lidas' : 'Lidas',
      tone: 'neutral',
      Icon: readStatusFilter === 'unread' ? Eye : MailOpen,
      onRemove: () => onReadStatusFilterChange('all'),
    });
  }
  // Agente
  if (agentFilter !== 'all' && onAgentFilterChange) {
    const agentName =
      agentFilter === 'none'
        ? 'Sem agente'
        : agents.find((a) => a.id === agentFilter)?.full_name || 'Agente';
    activeFilterChips.push({
      key: 'agent',
      label: agentName,
      tone: 'neutral',
      Icon: UserCircle,
      onRemove: () => onAgentFilterChange('all'),
    });
  }
  // Tag
  if (tagFilter !== 'all' && onTagFilterChange) {
    const tagName = tags.find((t) => t.id === tagFilter)?.name || 'Tag';
    activeFilterChips.push({
      key: 'tag',
      label: tagName,
      tone: 'neutral',
      Icon: Tag,
      onRemove: () => onTagFilterChange('all'),
    });
  }
  // Categoria
  if (categoryFilter !== 'all' && onCategoryFilterChange) {
    const catName = categories.find((c) => c.id === categoryFilter)?.name || 'Categoria';
    activeFilterChips.push({
      key: 'cat',
      label: catName,
      tone: 'neutral',
      Icon: LayoutGrid,
      onRemove: () => onCategoryFilterChange('all'),
    });
  }
  // Fase
  if (phaseFilter !== 'all' && onPhaseFilterChange) {
    const phaseName = phases.find((p) => p.id === phaseFilter)?.name || 'Fase';
    activeFilterChips.push({
      key: 'phase',
      label: phaseName,
      tone: 'neutral',
      Icon: Kanban,
      onRemove: () => onPhaseFilterChange('all'),
    });
  }

  /** Reset de todos os filtros (mantém escopo Meus/Todos/Bot inalterado). */
  const handleClearAllFilters = () => {
    onStatusFiltersChange(['all']);
    onInboxFilterChange?.('all');
    onDepartmentFilterChange?.('all');
    onConversationTypeFilterChange?.('all');
    onReadStatusFilterChange?.('all');
    onAgentFilterChange?.('all');
    onTagFilterChange?.('all');
    onCategoryFilterChange?.('all');
    onPhaseFilterChange?.('all');
  };

  
  // Ícone do canal por tipo
  const getChannelIcon = (channelType: string) => {
    switch (channelType) {
      case 'whatsapp':
      case 'whatsapp_oficial':
        return <MessageSquare className="h-3.5 w-3.5 text-green-600" />;
      case 'instagram':
        return <Instagram className="h-3.5 w-3.5 text-pink-500" />;
      case 'messenger':
        return <MessagesSquare className="h-3.5 w-3.5 text-blue-500" />;
      case 'telegram':
        return <Send className="h-3.5 w-3.5 text-sky-500" />;
      case 'webchat':
        return <Globe className="h-3.5 w-3.5 text-muted-foreground" />;
      default:
        return <Inbox className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  /**
   * Badge pequeno do canal exibido sobre o avatar da conversa.
   * - WhatsApp não-oficial: verde (#25D366)
   * - WhatsApp Oficial / WABA (channel_type 'whatsapp_oficial' ou provider Meta): preto
   * - Instagram: rosa
   * - Messenger / Telegram / Webchat: ícones Lucide com cores de marca
   * Performance: render puro de SVG inline, sem fetch nem state. Custo desprezível.
   */
  const getChannelBadge = (channelType?: string | null, providerSlug?: string | null) => {
    if (!channelType) return null;
    // Detecta WABA apenas quando o canal é WhatsApp. Instagram (meta-instagram)
    // também usa provider "meta-*", então restringimos o match por providerSlug
    // ao channel_type 'whatsapp' para não classificar Instagram como WABA.
    const isWaba =
      channelType === 'whatsapp_oficial' ||
      (channelType === 'whatsapp' && !!providerSlug && /meta|waba|cloud/i.test(providerSlug));

    // Glyph SVG do WhatsApp (reutilizado para verde e preto)
    const WhatsAppGlyph = ({ color }: { color: string }) => (
      <svg viewBox="0 0 32 32" className="h-[14px] w-[14px]" aria-hidden="true">
        <path
          fill={color}
          d="M16 2.667C8.636 2.667 2.667 8.636 2.667 16c0 2.355.621 4.66 1.8 6.687L2.667 29.333l6.84-1.78A13.31 13.31 0 0 0 16 29.333C23.364 29.333 29.333 23.364 29.333 16S23.364 2.667 16 2.667Zm7.74 18.86c-.328.92-1.62 1.694-2.66 1.92-.71.15-1.638.27-4.762-1.024-3.996-1.654-6.57-5.71-6.77-5.974-.2-.264-1.6-2.124-1.6-4.05 0-1.928 1.012-2.872 1.37-3.26.296-.32.78-.464 1.246-.464.15 0 .286.008.41.014.358.016.538.036.774.602.296.71 1.006 2.464 1.094 2.644.09.18.18.426.054.69-.118.27-.224.382-.404.59-.18.21-.35.37-.53.6-.164.198-.348.41-.14.762.21.35.928 1.532 1.99 2.478 1.37 1.22 2.5 1.61 2.86 1.768.27.118.59.094.79-.118.252-.27.566-.722.886-1.166.226-.318.51-.358.812-.244.31.106 1.95.918 2.288 1.084.338.166.56.246.644.388.082.146.082.842-.244 1.62Z"
        />
      </svg>
    );

    let inner: JSX.Element;
    if (isWaba) {
      // WABA: preto no tema claro, branco no escuro (via currentColor)
      inner = (
        <span className="text-foreground inline-flex">
          <WhatsAppGlyph color="currentColor" />
        </span>
      );
    } else if (channelType === 'whatsapp') {
      inner = <WhatsAppGlyph color="#25D366" />;
    } else if (channelType === 'instagram') {
      inner = <Instagram className="h-[14px] w-[14px] text-pink-500" strokeWidth={2.5} />;
    } else if (channelType === 'messenger') {
      inner = <MessagesSquare className="h-[14px] w-[14px] text-blue-500" strokeWidth={2.5} />;
    } else if (channelType === 'telegram') {
      inner = <Send className="h-[14px] w-[14px] text-sky-500" strokeWidth={2.5} />;
    } else if (channelType === 'webchat') {
      inner = <Globe className="h-[14px] w-[14px] text-muted-foreground" strokeWidth={2.5} />;
    } else {
      inner = <Inbox className="h-[14px] w-[14px] text-muted-foreground" strokeWidth={2.5} />;
    }

    // Badge: ícone maior, padding mínimo e deslocado para baixo do avatar
    return (
      <div
        className="absolute -bottom-1.5 -left-1 z-10 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-background border border-border shadow-sm"
        aria-label={`Canal: ${channelType}`}
      >
        {inner}
      </div>
    );
  };
  
  // Colapsa pesquisa quando perde foco e está vazia
  const handleSearchBlur = () => {
    if (!searchQuery.trim()) {
      setIsSearchExpanded(false);
    }
  };

  // Helper para iniciais do avatar
  const getInitials = (name: string | null) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Formata o tempo relativo da última mensagem
  // Menos de 1 minuto: mostra segundos (ex: "30s")
  // 1 minuto ou mais: usa formatDistanceToNow normal
  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    // Se menos de 60 segundos, mostrar segundos
    if (diffInSeconds < 60) {
      return `${Math.max(1, diffInSeconds)}s`;
    }
    
    // Para tempos maiores, usar formatDistanceToNow
    const timeStr = formatDistanceToNow(date, { 
      addSuffix: false, 
      locale: ptBR 
    });
    return timeStr.replace(/^cerca de /i, '');
  };

  // Trunca o preview da mensagem - limite reduzido para garantir espaço para badge de contagem e timestamp
  const truncatePreview = (text: string | null, maxLength: number = 25) => {
    if (!text) return 'Sem mensagens';
    // Notas internas gravam menções como `@[Nome](uuid)`; no preview mostramos só "@Nome"
    const clean = stripMentionIds(text);
    if (clean.length <= maxLength) return clean;
    return clean.slice(0, maxLength) + '...';
  };

  // Trunca o nome da conversa para evitar problemas de layout
  const truncateName = (name: string | null, maxLength: number = 22) => {
    if (!name) return '';
    if (name.length <= maxLength) return name;
    return name.slice(0, maxLength) + '...';
  };

  // Nome do departamento selecionado para exibir no botão
  const selectedDeptName = departmentFilter === 'all' 
    ? 'Todos Departamentos' 
    : departments.find(d => d.id === departmentFilter)?.name || 'Departamento';

  return (
    <div className="w-full md:w-[21.6rem] h-full bg-card flex flex-col rounded-lg shadow-sm border">
      {/* =====================================================================
          HEADER REDESENHADO v1.1.0 — "Segmented + chips roláveis"
          - Linha 1: Título + badge + ações (Status WA, Nova Conversa, Kanban)
          - Linha 2: Segmented control de escopo (Meus / Todos / Bot)
          - Linha 3: Busca sempre visível + botão Filtros (abre Sheet)
          - Linha 4: Chips de filtros ativos (scroll horizontal)
          Todos os filtros legados (Status, Inbox, Tipo, Depto, Leitura,
          Agente, Tag, Categoria, Fase) foram movidos para o Sheet lateral.
         ===================================================================== */}
      <div className="p-3 space-y-2.5">
        {/* Linha 1: Título + ações */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {/* Ícone de Status do WhatsApp */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setStatusSheetOpen(true)}
                >
                  <WhatsAppStatusIcon size={18} className="text-muted-foreground hover:text-primary transition-colors" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Status do WhatsApp</TooltipContent>
            </Tooltip>

            {/* Título da tela de chat (renomeado de "Conversas" para "Chat") */}
            <h2 className="font-semibold text-lg">Chat</h2>
            <Badge variant="secondary" className="ml-1">{totalCount ?? conversations.length}</Badge>
          </div>

          <div className="flex items-center gap-0.5">
            {tenantId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setNewConversationDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 text-primary" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Nova Conversa</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => navigate('/tenant/kanban')}
                >
                  <Kanban className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Abrir Kanban</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Linha 2: Segmented control de escopo (Meus / Todos / Bot) */}
        {onScopeFilterChange && (
          <div
            className="grid grid-cols-3 gap-0.5 p-0.5 bg-muted/60 rounded-md"
            role="tablist"
            aria-label="Escopo de conversas"
          >
            {([
              { value: 'all', label: 'Todos', Icon: MessagesSquare },
              { value: 'bot', label: 'Bot', Icon: Bot },
              { value: 'mine', label: 'Meus', Icon: User },
            ] as const).map(({ value, label, Icon }) => {
              const active = scopeFilter === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onScopeFilterChange(value)}
                  className={cn(
                    'h-7 inline-flex items-center justify-center gap-1.5 rounded-[5px] text-xs font-medium transition-all',
                    active
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Linha 3: Busca moderna + Filtros ícone-only */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60 pointer-events-none" />
            <Input
              placeholder="Buscar conversas..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={cn(
                'pl-9 pr-8 h-9 text-sm rounded-xl border-0 bg-muted/40 shadow-none',
                'ring-1 ring-inset ring-border/40',
                'placeholder:text-muted-foreground/50',
                'focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-primary/20'
              )}
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground/70 hover:text-foreground"
                onClick={() => onSearchChange('')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-xl flex-shrink-0 relative border-0 ring-1 ring-inset ring-border/40 bg-muted/40 hover:bg-muted/60"
                onClick={() => setFiltersSheetOpen(true)}
              >
                <Filter className="h-3.5 w-3.5" />
                {totalActiveFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground leading-none">
                    {totalActiveFiltersCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Filtros avançados</TooltipContent>
          </Tooltip>
        </div>

        {/* Linha 4: Chips de filtros ativos (scroll horizontal) */}
        {activeFilterChips.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-0.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className={cn(
                  'group inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-medium whitespace-nowrap flex-shrink-0 transition-colors',
                  chip.tone === 'primary'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/15'
                    : 'bg-muted text-foreground hover:bg-muted/70'
                )}
                title={`Remover filtro: ${chip.label}`}
              >
                {chip.Icon && <chip.Icon className="h-3 w-3" />}
                <span>{chip.label}</span>
                <X className="h-3 w-3 opacity-60 group-hover:opacity-100" />
              </button>
            ))}
            {activeFilterChips.length >= 2 && (
              <button
                type="button"
                onClick={handleClearAllFilters}
                className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-medium text-muted-foreground hover:text-destructive whitespace-nowrap flex-shrink-0"
              >
                Limpar tudo
              </button>
            )}
          </div>
        )}
      </div>

      {/* =====================================================================
          Sheet de Filtros Avançados (lateral esquerdo)
          Centraliza TODOS os filtros antes inline: Inbox, Status, Tipo,
          Departamento, Leitura + Agente, Tag, Categoria, Fase.
         ===================================================================== */}
      <Sheet open={filtersSheetOpen} onOpenChange={setFiltersSheetOpen}>
        <SheetContent side="left" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtros avançados</SheetTitle>
            <SheetDescription>
              Refine a lista de conversas combinando os filtros abaixo.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            {/* Caixa (Inbox) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Caixa de entrada</label>
              <Select
                value={inboxFilter || 'all'}
                onValueChange={(value) => onInboxFilterChange?.(value as string | 'all')}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Todas as Caixas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>Todas as Caixas</span>
                    </div>
                  </SelectItem>
                  {inboxes.length > 0 && <SelectSeparator />}
                  {inboxes.map((inbox) => (
                    <SelectItem key={inbox.id} value={inbox.id}>
                      <div className="flex items-center gap-2 w-full">
                        {getChannelIcon(inbox.channel_type)}
                        <span className="truncate flex-1">{inbox.name}</span>
                        {inbox.status && (
                          <span
                            className={cn(
                              'h-2 w-2 rounded-full flex-shrink-0',
                              inbox.status === 'active' && 'bg-green-500',
                              inbox.status === 'connecting' && 'bg-blue-500',
                              inbox.status === 'disconnected' && 'bg-orange-500',
                              inbox.status === 'error' && 'bg-destructive',
                              inbox.status === 'inactive' && 'bg-muted-foreground/50'
                            )}
                          />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status (multi-seleção) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => onStatusFiltersChange(['all'])}
                  className={cn(
                    'h-7 px-2.5 rounded-full text-xs border transition-colors',
                    statusFilters.includes('all')
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-background border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  Todas
                </button>
                {(['pending', 'open', 'closed', 'spam'] as ConversationStatus[]).map((status) => {
                  const isSelected = statusFilters.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => {
                        let newFilters: (ConversationStatus | 'all')[];
                        if (isSelected) {
                          newFilters = statusFilters.filter((s) => s !== status && s !== 'all');
                          if (newFilters.length === 0) newFilters = ['all'];
                        } else {
                          newFilters = [...statusFilters.filter((s) => s !== 'all'), status];
                        }
                        onStatusFiltersChange(newFilters);
                      }}
                      className={cn(
                        'h-7 px-2.5 rounded-full text-xs border inline-flex items-center gap-1.5 transition-colors',
                        isSelected
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'bg-background border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <span className={cn('w-2 h-2 rounded-full', statusColors[status])} />
                      {statusLabels[status]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tipo de conversa */}
            {onConversationTypeFilterChange && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Tipo</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['all', 'individual', 'group'] as ConversationType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => onConversationTypeFilterChange(t)}
                      className={cn(
                        'h-8 rounded-md text-xs border inline-flex items-center justify-center gap-1.5 transition-colors',
                        conversationTypeFilter === t
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'bg-background border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {t === 'group' ? <Users className="h-3 w-3" /> : t === 'individual' ? <User className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                      {typeLabels[t]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Departamento */}
            {onDepartmentFilterChange && departments.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Departamento</label>
                <Select
                  value={departmentFilter}
                  onValueChange={(v) => onDepartmentFilterChange(v as string | 'all')}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Todos Departamentos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Departamentos</SelectItem>
                    <SelectItem value="none">Sem Departamento</SelectItem>
                    <SelectSeparator />
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3 text-blue-600" />
                          {dept.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Status de leitura */}
            {onReadStatusFilterChange && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Leitura</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['all', 'read', 'unread'] as const).map((rs) => (
                    <button
                      key={rs}
                      type="button"
                      onClick={() => onReadStatusFilterChange(rs)}
                      className={cn(
                        'h-8 rounded-md text-xs border inline-flex items-center justify-center gap-1.5 transition-colors',
                        readStatusFilter === rs
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'bg-background border-border text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {rs === 'unread' ? <Eye className="h-3 w-3" /> : rs === 'read' ? <MailOpen className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                      {rs === 'all' ? 'Todas' : rs === 'read' ? 'Lidas' : 'Não lidas'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Agente */}
            {onAgentFilterChange && (
              <AgentFilterPopover
                agents={agents}
                agentFilter={agentFilter}
                onAgentFilterChange={onAgentFilterChange}
              />
            )}

            {/* Tag */}
            {onTagFilterChange && (
              <TagFilterPopover
                tags={tags}
                tagFilter={tagFilter}
                onTagFilterChange={onTagFilterChange}
              />
            )}

            {/* Categoria */}
            {onCategoryFilterChange && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Categoria</label>
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => onCategoryFilterChange(value as string | 'all')}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Todas as Categorias" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <div className="flex items-center gap-2">
                        <LayoutGrid className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>Todas as Categorias</span>
                      </div>
                    </SelectItem>
                    {categories.length > 0 && <SelectSeparator />}
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: category.color }} />
                          <span className="truncate">{category.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Fase (depende da categoria) */}
            {onPhaseFilterChange && (
              <PhaseFilterSelect
                phases={phases}
                phaseFilter={phaseFilter}
                categoryFilter={categoryFilter}
                onPhaseFilterChange={onPhaseFilterChange}
              />
            )}
          </div>

          <SheetFooter className="mt-6 flex-row gap-2 sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              disabled={totalActiveFiltersCount === 0}
              className="flex-1"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Limpar filtros
            </Button>
            <Button size="sm" onClick={() => setFiltersSheetOpen(false)} className="flex-1">
              Aplicar
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>


      {/* Conversation List */}
      <ScrollArea ref={scrollContainerRef} className="flex-1">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="rounded-full bg-muted p-3 mb-3">
              <MessageSquare className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              Nenhuma conversa encontrada
            </p>
          </div>
        ) : (
          <div className="p-2 pr-3 space-y-1">
            {conversations.map(conversation => (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation)}
                className={cn(
                  // v1.2.5: borda de destaque ao redor da conversa selecionada (padrão WhatsApp Web)
                  'group w-full p-3 rounded-lg text-left transition-colors relative',
                  'border border-transparent',
                  'hover:bg-accent/50',
                  selectedConversation?.id === conversation.id &&
                    'bg-accent border-foreground/70 ring-1 ring-foreground/20'
                )}
              >
                {/* v1.2.4: alfinete sem fundo, com efeito 3D via drop-shadow e gradiente */}
                {conversation.is_pinned && (
                  <div
                    className="absolute bottom-1 left-1 flex items-center justify-center z-10 -rotate-12"
                    style={{
                      filter:
                        'drop-shadow(0 1px 0 rgba(0,0,0,0.35)) drop-shadow(0 2px 3px rgba(0,0,0,0.45)) drop-shadow(0 0 1px rgba(255,255,255,0.6))',
                    }}
                  >
                    <Pin
                      className="h-[14px] w-[14px] fill-red-500 stroke-red-900"
                      strokeWidth={1.5}
                      style={{
                        // gradiente sutil para efeito 3D (clareia o topo, escurece a base)
                        fill: 'url(#pin-3d-gradient)',
                      }}
                    />
                    {/* SVG defs com gradiente reutilizável para o pino 3D */}
                    <svg width="0" height="0" className="absolute">
                      <defs>
                        <linearGradient id="pin-3d-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#fca5a5" />
                          <stop offset="45%" stopColor="#ef4444" />
                          <stop offset="100%" stopColor="#7f1d1d" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="relative">
                    {/* Avatar diferenciado para grupos - com efeito de hover */}
                    <Avatar className="h-10 w-10 transition-all group-hover:scale-110 duration-200">
                      {conversation.group_id ? (
                        <>
                          <AvatarImage src={getStorageUrl(conversation.group?.avatar_url)} />
                          <AvatarFallback className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm">
                            {conversation.group ? getInitials(conversation.group.name) : <Users className="h-5 w-5" />}
                          </AvatarFallback>
                        </>
                      ) : (
                        <>
                          <AvatarImage src={getStorageUrl(conversation.contact?.avatar_url)} />
                          <AvatarFallback className={cn("text-sm", getAvatarColorClasses(conversation.contact?.name))}>
                            {getInitials(conversation.contact?.name)}
                          </AvatarFallback>
                        </>
                      )}
                    </Avatar>
                    <div
                      className={cn(
                        'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card',
                        statusColors[conversation.status]
                      )}
                    />
                    {/* Badge do tipo de canal (WhatsApp, WABA, Instagram, etc.) no canto inferior-esquerdo */}
                    {getChannelBadge(conversation.inbox?.channel_type, conversation.inbox?.provider_slug)}
                  </div>

                  {/* 
                    ═══════════════════════════════════════════════════════════════════════════════
                    ⚠️ ÁREA CRÍTICA DE TRUNCAMENTO - NÃO MODIFICAR SEM TESTAR ⚠️
                    
                    Regras CSS obrigatórias para truncamento funcionar em flex containers:
                    
                    1. Container principal: flex-1 min-w-0 overflow-hidden
                       - min-w-0: Permite shrink abaixo do tamanho do conteúdo (flex default é min-width:auto)
                       - overflow-hidden: Corta conteúdo excedente
                    
                    2. Preview da mensagem: truncate min-w-0 max-w-full flex-1
                       - Combinação OBRIGATÓRIA para truncamento em flex
                       - NÃO REMOVER nenhuma dessas classes
                    
                    3. Nome do contato: truncate min-w-0
                       - Mesma regra do preview
                    
                    Histórico: Corrigido 6x em Dezembro/2024 - Esta é a correção definitiva
                    ═══════════════════════════════════════════════════════════════════════════════
                  */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between gap-2">
                      {/* Nome do contato/grupo - truncamento via CSS apenas (responsivo) */}
                      <span className="font-medium text-sm truncate min-w-0 block max-w-[150px]">
                        {conversation.group_id 
                          ? conversation.group?.name || 'Grupo' 
                          : conversation.contact?.name || conversation.contact?.phone || 'Contato'}
                      </span>
                      {/* v1.2.1: alfinete movido para o avatar (canto superior-esquerdo) */}
                      {conversation.last_message_at && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                          {formatTime(conversation.last_message_at)}
                        </span>
                      )}
                    </div>
                    
                    {/* Container do preview - overflow-hidden é OBRIGATÓRIO */}
                    <div className="flex items-center justify-between gap-2 mt-0.5 overflow-hidden">
                      {/* ⚠️ CLASSES CRÍTICAS: truncate min-w-0 max-w-full flex-1 - NÃO REMOVER */}
                      <p className="text-xs text-muted-foreground truncate min-w-0 max-w-full flex-1">
                        {truncatePreview(conversation.last_message_preview)}
                      </p>
                      {/* ⚠️ Badge DEVE ter flex-shrink-0 para não ser comprimido por previews longos */}
                      {conversation.unread_count > 0 && (
                        <Badge className="h-5 min-w-5 px-1.5 text-xs flex-shrink-0 mr-1">
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </div>

                    {/* Badges de Inbox, Origem (Comentário), Departamento, Grupo e Avatar do Agente */}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {conversation.inbox.name}
                      </Badge>
                      {/* Badge de Comentário (Instagram) */}
                      {conversation.source_type === 'comment' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800"
                        >
                          <MessageSquare className="h-2.5 w-2.5 mr-0.5" />
                          Coment.
                        </Badge>
                      )}
                      {/* Badge de Menção (Instagram) */}
                      {conversation.source_type === 'mention' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-800"
                        >
                          @Menção
                        </Badge>
                      )}
                      {/* Badge de Story (Instagram) */}
                      {conversation.source_type === 'story' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-800"
                        >
                          Story
                        </Badge>
                      )}
                      {/* Badge de Grupo */}
                      {conversation.group_id && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200"
                        >
                          <Users className="h-2.5 w-2.5 mr-0.5" />
                          Grupo
                        </Badge>
                      )}
                      {/* Ícone de Distribuição Automática Ativa (para grupos) */}
                      {conversation.group?.auto_distribution_enabled && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Zap className="h-3.5 w-3.5 text-amber-500 fill-amber-200 flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Distribuição automática ativa
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {/* Badge de Campanha (somente campanha) */}
                      {conversation.has_non_campaign_messages === false && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200"
                        >
                          <Megaphone className="h-2.5 w-2.5 mr-0.5" />
                          Campanha
                        </Badge>
                      )}
                      {/* Badge de Departamento */}
                      {conversation.department && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200"
                        >
                          <Building className="h-2.5 w-2.5 mr-0.5" />
                          {conversation.department.name}
                        </Badge>
                      )}
                      {/* Avatar do Agente Responsável */}
                      {conversation.assigned_agent && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Avatar className="h-5 w-5 border border-green-300">
                              <AvatarImage 
                                src={conversation.assigned_agent.avatar_url ? getStorageUrl(conversation.assigned_agent.avatar_url) : undefined} 
                                alt={conversation.assigned_agent.full_name || 'Agente'} 
                              />
                              <AvatarFallback className="text-[8px] bg-green-100 text-green-700">
                                {getInitials(conversation.assigned_agent.full_name || 'A')}
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {conversation.assigned_agent.full_name || 'Agente'}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>

                    {/* Tags do Contato (compactas) */}
                    {!conversation.group_id && conversation.contact?.tags && conversation.contact.tags.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {conversation.contact.tags.slice(0, 2).map(tag => (
                          <div 
                            key={tag.id}
                            className="flex items-center gap-1 text-[10px] font-medium"
                            style={{ color: tag.color }}
                          >
                            <Tag size={10} />
                            <span className="max-w-[60px] truncate">{tag.name}</span>
                          </div>
                        ))}
                        {conversation.contact.tags.length > 2 && (
                          <Badge 
                            variant="secondary" 
                            className="text-[10px] px-1.5 py-0"
                            title={conversation.contact.tags.slice(2).map(t => t.name).join(', ')}
                          >
                            +{conversation.contact.tags.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}

            {/* Sentinel para IntersectionObserver - Infinite Scroll (padrão QueueKanbanColumn) */}
            {hasMore && <div ref={loadMoreRef} className="h-4" />}
            {isFetchingMore && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Dialog para nova conversa */}
      {tenantId && (
        <StartNewConversationDialog
          open={newConversationDialogOpen}
          onOpenChange={setNewConversationDialogOpen}
          tenantId={tenantId}
        />
      )}

      {/* Sheet de Status do WhatsApp */}
      <WhatsAppStatusSheet
        open={statusSheetOpen}
        onOpenChange={setStatusSheetOpen}
        tenantId={tenantId}
      />
    </div>
  );
}